// src/components/payments/RequireEntitlement.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { doc, onSnapshot, Timestamp } from "firebase/firestore";
import { db } from "../../firebase/firebaseServices";
import { useAuth } from "../../firebase/AuthContext";

const ENTITLED_LAND_PATH = "/my-nutrition-status";
const NOT_ENTITLED_PATH = "/home";
const MAX_TIMEOUT_MS = 2147483647;

// Routes where we temporarily tolerate "not entitled" to allow post-checkout writes
const DEFAULT_SKIP_REDIRECT_PATHS = ["/checkout/success", "/checkout/cancel"];

/**
 * Small helper class that encapsulates entitlement decisions.
 * It inspects the trial fields, all subscriptions, entitlementExtensionUntil,
 * and gracePeriod and returns { entitled, until }.
 */
class EntitlementCalculator {
  constructor(currentDate) {
    this.currentDate = currentDate instanceof Date ? currentDate : new Date();
  }

  /**
   * Normalize a Firestore Timestamp | Date | string | null into a JS Date (or null).
   */
  static asDateOrNull(value) {
    if (!value) return null;
    if (value instanceof Timestamp) {
      return value.toDate();
    }
    if (value instanceof Date) {
      return value;
    }
    if (typeof value === "string" || typeof value === "number") {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    return null;
  }

  /**
   * Derive status flags from a raw subscription status string.
   */
  static getStatusFlags(rawStatus) {
    const normalized = String(rawStatus || "").toUpperCase();
    const activeStatuses = ["ACTIVE", "TRIALING", "APPROVAL_PENDING"];
    const pausedOrCancelledStatuses = ["SUSPENDED", "CANCELLED"];

    const isStatusActive = activeStatuses.includes(normalized);
    const isStatusPausedOrCancelled = pausedOrCancelledStatuses.includes(
      normalized
    );

    return {
      normalized,
      isStatusActive,
      isStatusPausedOrCancelled,
    };
  }

  /**
   * Trial entitlement:
   * - Flag isTrialPeriodActive must be true
   * - endTrialPeriodDate must exist and be in the future
   */
  evaluateTrialPeriod(userData) {
    const now = this.currentDate;
    const rawEndTrial = userData?.endTrialPeriodDate;
    const trialEndDate = EntitlementCalculator.asDateOrNull(rawEndTrial);
    const isTrialFlagTrue = userData?.isTrialPeriodActive === true;

    const isTrialEntitled = !!isTrialFlagTrue && !!trialEndDate && trialEndDate > now;

    return {
      isTrialEntitled,
      trialEndDate: isTrialEntitled ? trialEndDate : null,
    };
  }

  /**
   * Decide whether a single subscription grants entitlement and for how long.
   *
   * Rules:
   * - If status is ACTIVE/TRIALING/APPROVAL_PENDING: entitled if nextBill is missing OR in the future
   * - If status is SUSPENDED/CANCELLED: entitled only if nextBill exists and is in the future
   *
   * Returns:
   *   { isEntitled: boolean, entitlementEndDate: Date | null }
   */
  evaluateSingleSubscription(subscription) {
    const now = this.currentDate;
    const safeSub = subscription || {};

    const statusFlags = EntitlementCalculator.getStatusFlags(
      safeSub.status || safeSub.activeSubscriptionStatus
    );

    const nextBillDate = EntitlementCalculator.asDateOrNull(
      safeSub.nextBillingTime
    );
    const isNextBillFuture = !!nextBillDate && nextBillDate > now;

    const isEntitled =
      (statusFlags.isStatusActive && (!nextBillDate || isNextBillFuture)) ||
      (statusFlags.isStatusPausedOrCancelled && isNextBillFuture);

    // If there is no next billing but the subscription is active, treat as "indefinite" (null end).
    const entitlementEndDate = isEntitled ? nextBillDate || null : null;

    return { isEntitled, entitlementEndDate };
  }

  /**
   * Evaluate all subscriptions and return:
   *   - isAnySubscriptionEntitled: whether any sub grants entitlement
   *   - paidEntitlementEndDateEarliest: earliest finite end among all paid subs
   *   - paidEntitlementEndDateLatest: latest finite end among all paid subs
   */
  evaluateSubscriptions(subscriptions) {
    const list = Array.isArray(subscriptions) ? subscriptions : [];

    let isAnySubscriptionEntitled = false;
    let earliestPaidEndDate = null;
    let latestPaidEndDate = null;

    for (const subscription of list) {
      const { isEntitled, entitlementEndDate } =
        this.evaluateSingleSubscription(subscription);

      if (!isEntitled) continue;

      isAnySubscriptionEntitled = true;

      if (entitlementEndDate instanceof Date) {
        const time = entitlementEndDate.getTime();

        if (!earliestPaidEndDate || time < earliestPaidEndDate.getTime()) {
          earliestPaidEndDate = entitlementEndDate;
        }
        if (!latestPaidEndDate || time > latestPaidEndDate.getTime()) {
          latestPaidEndDate = entitlementEndDate;
        }
      }
      // If entitlementEndDate is null, that means "indefinite". We keep it out of
      // earliest/latest and handle "indefinite" at the calculate() layer.
    }

    return {
      isAnySubscriptionEntitled,
      paidEntitlementEndDateEarliest: earliestPaidEndDate,
      paidEntitlementEndDateLatest: latestPaidEndDate,
    };
  }

  /**
   * Full calculation for a user document.
   * Combines:
   *   - trial entitlement
   *   - subscription entitlement (from ALL subscriptions)
   *   - entitlementExtensionUntil (stacking)
   *   - root-level gracePeriod flag
   *
   * Returns:
   *   { entitled: boolean, until: Date | null }
   */
  calculate(userData) {
    const trialPart = this.evaluateTrialPeriod(userData);
    const subscriptionsPart = this.evaluateSubscriptions(
      userData?.subscriptions
    );
    const hasRootGrace = userData?.gracePeriod === true;

    const extensionDate = EntitlementCalculator.asDateOrNull(
      userData?.entitlementExtensionUntil
    );
    const isExtensionEntitled =
      !!extensionDate && extensionDate > this.currentDate;

    const entitled = Boolean(
      trialPart.isTrialEntitled ||
        subscriptionsPart.isAnySubscriptionEntitled ||
        hasRootGrace ||
        isExtensionEntitled
    );

    // If any subscription is entitled and has no finite end (no nextBillingTime),
    // or gracePeriod is true with no end, treat entitlement as indefinite.
    if (entitled) {
      const hasAnyIndefiniteSub = Array.isArray(userData?.subscriptions)
        ? userData.subscriptions.some((subscription) => {
            const { isEntitled, entitlementEndDate } =
              this.evaluateSingleSubscription(subscription);
            return isEntitled && !entitlementEndDate;
          })
        : false;

      if (hasAnyIndefiniteSub || hasRootGrace) {
        return { entitled: true, until: null };
      }
    }

    // Otherwise, entitlement ends at the latest end of trial, paid subs, and extension.
    const endCandidates = [];

    if (trialPart.isTrialEntitled && trialPart.trialEndDate) {
      endCandidates.push(trialPart.trialEndDate.getTime());
    }

    if (
      subscriptionsPart.isAnySubscriptionEntitled &&
      subscriptionsPart.paidEntitlementEndDateLatest
    ) {
      endCandidates.push(
        subscriptionsPart.paidEntitlementEndDateLatest.getTime()
      );
    }

    if (isExtensionEntitled && extensionDate) {
      endCandidates.push(extensionDate.getTime());
    }

    const until =
      endCandidates.length > 0 ? new Date(Math.max(...endCandidates)) : null;

    return { entitled, until };
  }

  /**
   * Convenience static entry point.
   */
  static fromUserDocument(userData, referenceDate) {
    const calculator = new EntitlementCalculator(referenceDate || new Date());
    return calculator.calculate(userData || {});
  }
}

// Make the calculator reusable in payment services / UI
export { EntitlementCalculator };

export default function RequireEntitlement({
  children,
  autoLandWhenEntitled = false,
  skipRedirectPaths = DEFAULT_SKIP_REDIRECT_PATHS,
  graceRedirectMs = 6000,
}) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [isEntitled, setIsEntitled] = useState(false);
  const [entitledUntil, setEntitledUntil] = useState(null); // Date | null

  const redirectedRef = useRef(false);
  const deferTimerRef = useRef(0);

  const isSkipPath = skipRedirectPaths.some((pathPrefix) =>
    location.pathname.startsWith(pathPrefix)
  );

  /**
   * Pure function wrapper around EntitlementCalculator.
   */
  const computeEntitlement = useCallback((userData) => {
    return EntitlementCalculator.fromUserDocument(userData, new Date());
  }, []);

  // Single redirect sink (never double-navigate)
  const redirectToNotEntitled = useCallback(() => {
    if (redirectedRef.current) return;
    redirectedRef.current = true;

    if (location.pathname !== NOT_ENTITLED_PATH) {
      navigate(NOT_ENTITLED_PATH, {
        replace: true,
      });
    }
  }, [location.pathname, navigate]);

  // Realtime: react to Firestore changes immediately
  useEffect(() => {
    if (!currentUser?.uid) return;

    const userDocumentReference = doc(db, "users", currentUser.uid);

    const unsubscribe = onSnapshot(
      userDocumentReference,
      (snapshot) => {
        const data = snapshot.data() || {};
        const { entitled, until } = computeEntitlement(data);

        setIsEntitled(entitled);
        setEntitledUntil(until);

        // Clear any pending deferred redirect if entitlement was restored
        if (entitled && deferTimerRef.current) {
          clearTimeout(deferTimerRef.current);
          deferTimerRef.current = 0;
        }

        if (!entitled) {
          if (isSkipPath) {
            // Defer redirect briefly to allow post-checkout doc writes
            if (deferTimerRef.current) {
              clearTimeout(deferTimerRef.current);
            }

            deferTimerRef.current = window.setTimeout(
              () => redirectToNotEntitled(),
              Math.max(0, graceRedirectMs)
            );
          } else {
            redirectToNotEntitled();
          }
        } else if (autoLandWhenEntitled && location.pathname !== ENTITLED_LAND_PATH) {
          navigate(ENTITLED_LAND_PATH, {
            replace: true,
          });
        }
      },
      () => {
        // Snapshot errors are ignored; exact-timeout still covers expiry moment.
      }
    );

    return () => {
      if (deferTimerRef.current) {
        clearTimeout(deferTimerRef.current);
        deferTimerRef.current = 0;
      }
      unsubscribe();
    };
  }, [
    currentUser?.uid,
    computeEntitlement,
    redirectToNotEntitled,
    navigate,
    location.pathname,
    autoLandWhenEntitled,
    isSkipPath,
    graceRedirectMs,
  ]);

  // Exact-timeout: kick user out right when entitlement ends
  useEffect(() => {
    if (!isEntitled || !entitledUntil) return;

    const millisecondsUntilEnd = entitledUntil.getTime() - Date.now();
    if (millisecondsUntilEnd <= 0) {
      redirectToNotEntitled();
      return;
    }

    let chunkTimerId = 0;
    let finalTimerId = 0;

    const scheduleFinalRedirect = (delayMs) => {
      finalTimerId = window.setTimeout(() => {
        redirectToNotEntitled();
      }, Math.max(0, delayMs + 250)); // small buffer for jitter
    };

    if (millisecondsUntilEnd <= MAX_TIMEOUT_MS) {
      scheduleFinalRedirect(millisecondsUntilEnd);
    } else {
      const tick = () => {
        const remaining = entitledUntil.getTime() - Date.now();
        if (remaining <= MAX_TIMEOUT_MS) {
          clearTimeout(chunkTimerId);
          scheduleFinalRedirect(remaining);
        } else {
          chunkTimerId = window.setTimeout(tick, MAX_TIMEOUT_MS);
        }
      };
      chunkTimerId = window.setTimeout(tick, MAX_TIMEOUT_MS);
    }

    return () => {
      if (chunkTimerId) clearTimeout(chunkTimerId);
      if (finalTimerId) clearTimeout(finalTimerId);
    };
  }, [isEntitled, entitledUntil, redirectToNotEntitled]);

  return <>{children}</>;
}
